// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type InvestmentSummary } from '@shared/data-access/interfaces/redux/investments-state';
import { numberToFixed } from '@shared/ui-kit/utils';
import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '@advisor-app/store/store';
import { selectSummary } from '@advisor-app/store/slices/broker';

type AssetVehicle = Omit<InvestmentSummary, 'aum' | 'equity' | 'buyingPower'>;

const initialState: AssetVehicle = {
  stock: 0,
  etf: 0,
  mutual_fund: 0,
  cash: 0,
  other: 0,
};

export const useAssetVehicles = () => {
  const [assetsVehicles, setAssetsVehicles] = useState<AssetVehicle>(initialState);
  const summary = useAppSelector(selectSummary);

  const brokerEquity = summary.equity;

  const assetVehicleSummary = {
    stock: summary.stock,
    etf: summary.etf,
    mutual_fund: summary.mutual_fund,
    cash: summary.cash,
    other: summary.other,
  };

  const transformToPercentage = useCallback(
    (data: AssetVehicle) => {
      const percentages = {} as AssetVehicle;

      Object.keys(data).forEach(key => {
        const asset = key as keyof AssetVehicle;
        percentages[asset] = numberToFixed(data[asset] / brokerEquity, 4);
      });

      return percentages;
    },
    [brokerEquity],
  );

  useEffect(() => {
    const getAssetsVehicles = () => {
      const noData = Object.values(assetVehicleSummary).every(value => value === 0);
      if (noData) return;
      const percentages = transformToPercentage(assetVehicleSummary);
      setAssetsVehicles(percentages);
    };

    getAssetsVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  return { assetsVehicles };
};
